import { QuestionAnswerMap } from '@care-portals/api-interfaces';

const antiNauseaFollowupQuestions = [
  {
    field: 'antiNauseaFollowup_sex',
    label: 'What was your sex assigned at birth?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Male', key: 'male' },
        { value: 'Female', key: 'female' }
      ]
    },
    extras: { sendToBeluga: false }
  },
  {
    field: 'antiNauseaFollowup_healthChanges',
    label:
      'Have there been any health or medication changes, ED visits or hospitalizations since your last check-in?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No', key: 'no' },
        { value: 'Yes', key: 'yes' }
      ]
    }
  },
  {
    field: 'antiNauseaFollowup_healthChangesDetails',
    label: 'Please tell us more about the changes in your health',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'antiNauseaFollowup_healthChangesDetails'
    }
  },
  {
    field: 'antiNauseaFollowup_medicationDoing',
    label: 'How are you doing on the current medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Well', key: 'well' },
        { value: 'Not well', key: 'not_well' }
      ]
    }
  },
  {
    field: 'antiNauseaFollowup_medicationExperience',
    label: 'Please tell us more about what you are experiencing',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'antiNauseaFollowup_medicationExperience'
    }
  },
  {
    field: 'antiNauseaFollowup_sideEffects',
    label: 'Have you experienced any side effects from the current medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No', key: 'no' },
        { value: 'Yes', key: 'yes' }
      ]
    }
  },
  {
    field: 'antiNauseaFollowup_sideEffectsDetails',
    label: 'Please tell us more about the side effects you are experiencing',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'antiNauseaFollowup_sideEffectsDetails'
    }
  },
  {
    field: 'antiNauseaFollowup_treatmentPreference',
    label:
      'Would you like to continue your current treatment, change the dose, or change the medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: "I'm doing well and I would like to continue what I'm on",
          key: 'continue'
        },
        {
          value: 'I no longer need this medication',
          key: 'no_longer_need'
        },
        {
          value:
            "I'm not sure, have the provider reach out to discuss further",
          key: 'provider_reach_out'
        }
      ]
    }
  },
  {
    field: 'antiNauseaFollowup_additionalQuestions',
    label: 'What other information or questions do you have for the doctor?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'antiNauseaFollowup_additionalQuestions'
    }
  },
  {
    field: 'antiNauseaFollowup_truthfulnessConsent',
    label: 'Consent (Truthfulness):',
    type: 'select',
    educationalSlide:
      'Please attest to the following confirming that all information you have provided to us is true and complete.\n\nI verify that I am the patient and that I have answered the questions asked in this intake form. I confirm that I have reviewed and understood all the questions asked of me. I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor. I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not, in the information that I provided.',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do consent and wish to move forward',
          key: 'consent'
        },
        {
          value:
            'I have read the above information and I do not wish to continue',
          key: 'non_consent'
        }
      ]
    }
  },
  {
    field: 'antiNauseaFollowup_treatmentConsent',
    label: 'Consent:',
    type: 'select',
    educationalSlide: `You have reported experiencing nausea and/or vomiting, conditions that can significantly affect your comfort, daily activities, and overall quality of life. Ondansetron (Zofran) is a medication commonly prescribed to help prevent or relieve these symptoms by blocking certain chemical receptors in the body responsible for inducing nausea and vomiting.

The use of ondansetron may:
➢ Reduce or prevent episodes of nausea and vomiting
➢ Improve comfort and quality of life
➢ Help you maintain proper nutrition and hydration

While ondansetron is generally well-tolerated, possible side effects may include:
➢ Headache
➢ Constipation
➢ Dizziness or lightheadedness
➢ Fatigue or drowsiness
➢ Dry mouth
➢ Rare but serious side effects such as changes in heart rhythm (including QT prolongation) or severe allergic reactions (rash, itching, swelling)

You may need to avoid ondansetron or use it with caution if you:
➢ Have a known heart rhythm disorder, such as congenital long QT syndrome
➢ Are currently taking other medications that can affect the heart's rhythm or serotonin levels
➢ Have a history of severe allergic reactions to ondansetron or similar medications

Alternative treatment options for nausea and vomiting include:
➢ Dietary modifications (small, frequent meals; avoiding triggers)
➢ Ginger (such as ginger tea or candies)
➢ Acupressure or acupuncture
➢ Other prescription or over-the-counter anti-nausea medications
➢ Behavioral techniques (relaxation exercises, controlled breathing)

I understand the potential benefits, risks, and possible side effects of using ondansetron. I have been informed about alternative treatment options. I agree to inform my healthcare provider of any side effects or adverse reactions I may experience. I understand that this consent is voluntary and that I can withdraw from treatment at any time.

By selecting below, I consent to the use of ondansetron for the treatment of my nausea and/or vomiting.`,
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read and understand the information and I wish to proceed',
          key: 'proceed'
        },
        {
          value: 'I have read the information and I DO NOT wish to proceed',
          key: 'do_not_proceed'
        }
      ]
    }
  },
  {
    field: 'antiNauseaFollowup_complete',
    isLast: true,
    label:
      'Thank you for completing this questionnaire, please select "Finish" to submit',
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    field: 'antiNauseaFollowup_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    extras: { sendToBeluga: false },
    blockQuestionFlow: true,
    answer: {},
    type: 'info',
    isLast: true
  }
] as const;

export type AntiNauseaFollowupQuestionFieldAnswers = Partial<
  QuestionAnswerMap<typeof antiNauseaFollowupQuestions>
>;

export default antiNauseaFollowupQuestions;
